import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, PencilRuler, Send, Timer, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import AuthImg from "@/components/AuthImg";
import CalculationPad from "@/components/Calculator";
import { BILINGUAL_SUBJECTS } from "@/lib/subjects";

const btn = "inline-flex items-center gap-2 rounded-full border-[3px] border-slate-900 px-5 py-2.5 font-bold shadow-[3px_3px_0_0_#0F172A] disabled:opacity-50";

function QuestionCard({ q, paper, childId, value, onChange, onEnter, fr }) {
  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
      className="rounded-3xl border-4 border-slate-900 bg-white p-6 shadow-[6px_6px_0_0_#0F172A]" data-testid="tp-question">
      <p className="text-sm font-bold text-blue-700">Question {q.n}{q.marks ? ` · ${q.marks} ${fr ? "point(s)" : "mark(s)"}` : ""}</p>
      <p className="mt-2 text-xl sm:text-2xl font-bold whitespace-pre-wrap" data-testid="tp-question-text">{q.text}</p>
      {q.has_image && (
        <div className="mt-4 rounded-2xl border-2 border-slate-200 bg-white p-2 flex justify-center">
          <AuthImg src={`/test-papers/${paper.id}/image/${q.id}?child_id=${childId}&v=${q.img_v}`} className="max-h-80 object-contain" testId="tp-question-img" />
        </div>
      )}
      {q.typed ? (
        <input autoFocus value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onEnter()} maxLength={300}
          placeholder={fr ? "Tape ta réponse ici" : "Type your answer here"} data-testid="tp-answer-input"
          className="mt-5 w-full rounded-2xl border-[3px] border-slate-900 px-4 py-3 text-2xl font-bold outline-none focus:ring-4 focus:ring-yellow-300" />
      ) : (
        <p className="mt-5 rounded-2xl bg-amber-100 border-2 border-amber-300 px-4 py-3 font-semibold" data-testid="tp-paper-only">
          {fr ? "Celle-ci se fait sur le sujet imprimé (dessin, coloriage…). Passe à la suivante." : "Do this one on the printed paper (drawing, colouring…). Go to the next one."}
        </p>
      )}
    </motion.div>
  );
}

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function StartScreen({ paper, fr, onStart, onBack }) {
  return (
    <div className="max-w-xl mx-auto px-5 py-16 text-center" data-testid="tp-start">
      <img src="/mascot/kiko.png" alt="Kiko" className="w-28 h-28 mx-auto" />
      <h1 className="text-3xl sm:text-4xl font-bold mt-3">{paper.title}</h1>
      <p className="mt-2 text-lg">{paper.questions.length} questions · ⏱ {paper.duration_min} min</p>
      <p className="mt-4 text-slate-600">{fr ? "Comme à l'examen : quand le temps est fini, ta copie est rendue toute seule." : "Like a real exam: when time is up, your paper is handed in automatically."}</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button onClick={() => onStart(true)} className={`${btn} bg-yellow-300 text-lg`} data-testid="tp-start-timed"><Timer size={20} /> {fr ? `Avec chrono (${paper.duration_min} min)` : `With timer (${paper.duration_min} min)`}</button>
        <button onClick={() => onStart(false)} className={`${btn} bg-white text-lg`} data-testid="tp-start-free">{fr ? "Sans chrono" : "No timer"}</button>
      </div>
      <button onClick={onBack} className="mt-6 underline text-sm" data-testid="tp-start-back">{fr ? "Retour" : "Back"}</button>
    </div>
  );
}

function Countdown({ endAt, onEnd }) {
  const [left, setLeft] = useState(Math.max(0, Math.round((endAt - Date.now()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => {
      const l = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setLeft(l);
      if (l === 0) { clearInterval(t); onEnd(); }
    }, 500);
    return () => clearInterval(t);
  }, [endAt]); // eslint-disable-line
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border-[3px] border-slate-900 px-4 py-1.5 font-bold tabular-nums ${left <= 300 ? "bg-red-500 text-white animate-pulse" : "bg-white"}`} data-testid="tp-timer">
      <Timer size={18} /> {clock(left)}
    </span>
  );
}

function Results({ res, paper, fr, onBack, onRetry, used }) {
  const byId = Object.fromEntries(paper.questions.map((q) => [q.id, q]));
  return (
    <div className="max-w-3xl mx-auto px-5 py-8" data-testid="tp-results">
      <div className="text-center">
        <img src="/mascot/kiko.png" alt="Kiko" className="w-28 h-28 mx-auto" />
        <h1 className="text-4xl sm:text-5xl font-bold mt-2" data-testid="tp-score">{res.score} / {res.total}</h1>
        {used != null && <p className="text-sm text-blue-700 font-bold mt-1 inline-flex items-center gap-1" data-testid="tp-time-used"><Timer size={14} /> {fr ? "Temps" : "Time"} : {clock(used)}</p>}
        <p className="text-lg mt-1">{res.score >= res.total * 0.8 ? (fr ? "Excellent travail !" : "Excellent work!") : (fr ? "Bravo pour tes efforts ! Regarde les corrections." : "Well done for trying! Look at the corrections.")}</p>
      </div>
      <div className="mt-6 space-y-2">
        {res.results.filter((r) => r.typed).map((r) => (
          <div key={r.id} className={`rounded-2xl border-2 px-4 py-3 ${r.correct ? "border-green-500 bg-green-50" : "border-red-400 bg-red-50"}`} data-testid={`tp-result-${r.id}`}>
            <p className="font-bold flex items-center gap-2">{r.correct ? <Check className="text-green-600" size={18} /> : <X className="text-red-500" size={18} />} Question {r.n}</p>
            <p className="text-sm text-slate-600 line-clamp-2">{byId[r.id]?.text}</p>
            <p className="text-sm mt-1">{fr ? "Ta réponse" : "Your answer"}: <b>{r.given || "—"}</b>{!r.correct && <> · {fr ? "Bonne réponse" : "Correct answer"}: <b className="text-green-700" data-testid={`tp-result-expected-${r.id}`}>{r.expected}</b></>}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button onClick={onBack} className={`${btn} bg-white`} data-testid="tp-results-back">{fr ? "Retour" : "Back"}</button>
        <button onClick={onRetry} className={`${btn} bg-yellow-300`} data-testid="tp-results-retry">{fr ? "Recommencer" : "Try again"}</button>
      </div>
    </div>
  );
}

export default function TestPaperPlayer() {
  const { childId, paperId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [padOpen, setPadOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const runId = useRef(Date.now());
  const [mode, setMode] = useState(null);
  const [used, setUsed] = useState(null);
  const startedAt = useRef(Date.now());
  const submitRef = useRef(null);
  useEffect(() => {
    api.get(`/children/${childId}/test-papers/${paperId}`).then(({ data }) => setPaper(data))
      .catch((e) => { toast.error(formatError(e.response?.data?.detail) || "Not available"); navigate(`/kid/${childId}`); });
  }, [childId, paperId]); // eslint-disable-line
  if (!paper) return <div className="min-h-screen flex items-center justify-center bg-[#EAF7F4]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const fr = paper.subject === "French" || (BILINGUAL_SUBJECTS.includes(paper.subject) && localStorage.getItem("aa_voice_lang") === "fr");
  const back = () => navigate(`/kid/${childId}/subject/${encodeURIComponent(paper.subject)}`);
  if (res) return <div className="min-h-screen bg-[#EAF7F4] font-kids"><Results res={res} paper={paper} fr={fr} used={used} onBack={back} onRetry={() => { setRes(null); setAnswers({}); setI(0); setMode(null); setUsed(null); runId.current = Date.now(); }} /></div>;
  const chosen = mode || (paper.duration_min ? null : "free");
  if (!chosen) {
    return (
      <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900">
        <StartScreen paper={paper} fr={fr} onBack={back} onStart={(timed) => { startedAt.current = Date.now(); setMode(timed ? "timed" : "free"); }} />
      </div>
    );
  }

  const qs = paper.questions;
  const q = qs[i];
  const last = i === qs.length - 1;
  const isMaths = paper.subject === "Maths";
  const timed = chosen === "timed";
  const submit = async (force = false) => {
    if (busy) return;
    const blank = qs.filter((x) => x.typed && !(answers[x.id] || "").trim()).length;
    if (!force && blank && !window.confirm(fr ? `${blank} question(s) sans réponse. Terminer quand même ?` : `${blank} question(s) not answered. Finish anyway?`)) return;
    if (force) toast.info(fr ? "Temps écoulé ! Ta copie est rendue." : "Time is up! Your paper is handed in.");
    const seconds = Math.min(Math.round((Date.now() - startedAt.current) / 1000), (paper.duration_min || 360) * 60);
    setBusy(true);
    try {
      setRes((await api.post(`/children/${childId}/test-papers/${paperId}/submit`, { answers, timed, seconds })).data);
      setUsed(timed ? seconds : null);
      window.scrollTo(0, 0);
    }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  submitRef.current = submit;

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900" data-testid="tp-player">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex flex-wrap items-center gap-3">
        <button onClick={() => window.confirm(fr ? "Quitter ce sujet ?" : "Leave this test?") && back()} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="tp-exit"><ArrowLeft /></button>
        <h1 className="text-xl sm:text-2xl font-bold flex-1">{paper.title}</h1>
        {timed && <Countdown endAt={startedAt.current + paper.duration_min * 60000} onEnd={() => submitRef.current(true)} />}
        {isMaths && (
          <button onClick={() => setPadOpen((o) => !o)} className={`${btn} py-2 bg-white text-sm`} data-testid="tp-copybook-btn">
            <PencilRuler size={16} /> {padOpen ? (fr ? "Fermer" : "Close") : (fr ? "Cahier" : "Copybook")}
          </button>
        )}
      </header>
      <div className="max-w-5xl mx-auto px-5 mt-4 flex flex-wrap gap-1.5" data-testid="tp-dots">
        {qs.map((x, k) => (
          <button key={x.id} onClick={() => setI(k)} data-testid={`tp-dot-${k}`} title={`Question ${x.n}`}
            className={`h-8 min-w-8 px-2 rounded-full border-2 border-slate-900 text-xs font-bold ${k === i ? "bg-blue-600 text-white" : (answers[x.id] || "").trim() ? "bg-green-300" : "bg-white"}`}>{x.n}</button>
        ))}
      </div>
      <div className={`max-w-5xl mx-auto px-5 py-6 grid gap-6 ${padOpen ? "md:grid-cols-2" : ""}`}>
        <div>
          <AnimatePresence mode="wait">
            <QuestionCard key={q.id} q={q} paper={paper} childId={childId} fr={fr} value={answers[q.id] || ""} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
              onEnter={() => (last ? submit() : setI(i + 1))} />
          </AnimatePresence>
          <div className="mt-6 flex justify-between gap-3">
            <button disabled={i === 0} onClick={() => setI(i - 1)} className={`${btn} bg-white`} data-testid="tp-prev"><ArrowLeft size={18} /> {fr ? "Précédente" : "Previous"}</button>
            {last ? (
              <button disabled={busy} onClick={() => submit()} className={`${btn} bg-green-400`} data-testid="tp-submit">{busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {fr ? "Terminer" : "Finish"}</button>
            ) : (
              <button onClick={() => setI(i + 1)} className={`${btn} bg-yellow-300`} data-testid="tp-next">{fr ? "Suivante" : "Next"} <ArrowRight size={18} /></button>
            )}
          </div>
        </div>
        {padOpen && (
          <div data-testid="tp-copybook">
            <CalculationPad key={`tp_${paperId}_${runId.current}_${q.id}`} open setOpen={() => setPadOpen(false)} question={q.text} storageKey={`tp_${paperId}_${runId.current}_${q.id}`} lang={fr ? "fr" : "en"} />
          </div>
        )}
      </div>
    </div>
  );
}
